import React, { useState, useEffect } from 'react';
import { Match, MatchStatus, Matchday, Club } from '../../types/sports';
import { sportsApi } from '../../services/sportsApi';

interface MatchFormProps {
  match?: Match;
  onSave: (match: Omit<Match, 'id' | 'createdAt' | 'matchday' | 'homeClub' | 'awayClub'>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const MatchForm: React.FC<MatchFormProps> = ({ match, onSave, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    date: match?.date?.split('T')[0] || '',
    dateTime: match?.date ? new Date(match.date).toTimeString().slice(0, 5) : '',
    scheduledDate: match?.scheduledDate?.split('T')[0] || '',
    scheduledTime: match?.scheduledDate ? new Date(match.scheduledDate).toTimeString().slice(0, 5) : '',
    actualDate: match?.actualDate?.split('T')[0] || '',
    actualTime: match?.actualDate ? new Date(match.actualDate).toTimeString().slice(0, 5) : '',
    homeScore: match?.homeScore?.toString() || '',
    awayScore: match?.awayScore?.toString() || '',
    status: match?.status || MatchStatus.Scheduled,
    notes: match?.notes || '',
    matchdayId: match?.matchdayId || 0,
    homeClubId: match?.homeClubId || 0,
    awayClubId: match?.awayClubId || 0,
  });

  const [matchdays, setMatchdays] = useState<Matchday[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadMatchdays();
    loadClubs();
  }, []);

  const loadMatchdays = async () => {
    try {
      const data = await sportsApi.getMatchdays();
      setMatchdays(data);
    } catch (error) {
      console.error('Error loading matchdays:', error);
    }
  };

  const loadClubs = async () => {
    try {
      const data = await sportsApi.getClubs();
      setClubs(data);
    } catch (error) {
      console.error('Error loading clubs:', error);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Spieldatum ist erforderlich';
    }

    if (!formData.dateTime) {
      newErrors.dateTime = 'Spielzeit ist erforderlich';
    }

    if (!formData.scheduledDate) {
      newErrors.scheduledDate = 'Geplantes Datum ist erforderlich';
    }

    if (!formData.scheduledTime) {
      newErrors.scheduledTime = 'Geplante Uhrzeit ist erforderlich';
    }

    if (!formData.matchdayId) {
      newErrors.matchdayId = 'Spieltag ist erforderlich';
    }

    if (!formData.homeClubId) {
      newErrors.homeClubId = 'Heimverein ist erforderlich';
    }

    if (!formData.awayClubId) {
      newErrors.awayClubId = 'Auswärtsverein ist erforderlich';
    }

    if (formData.homeClubId === formData.awayClubId && formData.homeClubId !== 0) {
      newErrors.awayClubId = 'Auswärtsverein muss sich vom Heimverein unterscheiden';
    }

    if (formData.homeScore && isNaN(Number(formData.homeScore))) {
      newErrors.homeScore = 'Heimtore müssen eine Zahl sein';
    }

    if (formData.awayScore && isNaN(Number(formData.awayScore))) {
      newErrors.awayScore = 'Auswärtstore müssen eine Zahl sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const dateTime = new Date(`${formData.date}T${formData.dateTime}`).toISOString();
    const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`).toISOString();
    const actualDateTime = formData.actualDate && formData.actualTime
      ? new Date(`${formData.actualDate}T${formData.actualTime}`).toISOString()
      : undefined;

    onSave({
      date: dateTime,
      scheduledDate: scheduledDateTime,
      actualDate: actualDateTime,
      homeScore: formData.homeScore ? Number(formData.homeScore) : undefined,
      awayScore: formData.awayScore ? Number(formData.awayScore) : undefined,
      status: formData.status,
      notes: formData.notes || undefined,
      matchdayId: formData.matchdayId,
      homeClubId: formData.homeClubId,
      awayClubId: formData.awayClubId,
    });
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getStatusOptions = () => [
    { value: MatchStatus.Scheduled, label: 'Geplant' },
    { value: MatchStatus.Live, label: 'Live' },
    { value: MatchStatus.Finished, label: 'Beendet' },
    { value: MatchStatus.Postponed, label: 'Verschoben' },
    { value: MatchStatus.Cancelled, label: 'Abgesagt' },
  ];

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {match ? 'Spiel bearbeiten' : 'Neues Spiel'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={gridStyle}>
            {/* Spieltag */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Spieltag *</label>
              <select
                value={formData.matchdayId}
                onChange={(e) => handleInputChange('matchdayId', Number(e.target.value))}
                style={{
                  ...inputStyle,
                  ...(errors.matchdayId ? errorInputStyle : {})
                }}
              >
                <option value={0}>Spieltag auswählen</option>
                {matchdays.map(matchday => (
                  <option key={matchday.id} value={matchday.id}>
                    {matchday.name} - {matchday.season?.name || `Season ID: ${matchday.seasonId}`}
                  </option>
                ))}
              </select>
              {errors.matchdayId && <span style={errorTextStyle}>{errors.matchdayId}</span>}
            </div>

            {/* Heimverein */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Heimverein *</label>
              <select
                value={formData.homeClubId}
                onChange={(e) => handleInputChange('homeClubId', Number(e.target.value))}
                style={{
                  ...inputStyle,
                  ...(errors.homeClubId ? errorInputStyle : {})
                }}
              >
                <option value={0}>Heimverein auswählen</option>
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </select>
              {errors.homeClubId && <span style={errorTextStyle}>{errors.homeClubId}</span>}
            </div>

            {/* Auswärtsverein */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Auswärtsverein *</label>
              <select
                value={formData.awayClubId}
                onChange={(e) => handleInputChange('awayClubId', Number(e.target.value))}
                style={{
                  ...inputStyle,
                  ...(errors.awayClubId ? errorInputStyle : {})
                }}
              >
                <option value={0}>Auswärtsverein auswählen</option>
                {clubs.map(club => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </select>
              {errors.awayClubId && <span style={errorTextStyle}>{errors.awayClubId}</span>}
            </div>

            {/* Status */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', Number(e.target.value))}
                style={inputStyle}
              >
                {getStatusOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Spieldatum */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Spieldatum *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.date ? errorInputStyle : {})
                }}
              />
              {errors.date && <span style={errorTextStyle}>{errors.date}</span>}
            </div>

            {/* Spielzeit */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Spielzeit *</label>
              <input
                type="time"
                value={formData.dateTime}
                onChange={(e) => handleInputChange('dateTime', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.dateTime ? errorInputStyle : {})
                }}
              />
              {errors.dateTime && <span style={errorTextStyle}>{errors.dateTime}</span>}
            </div>

            {/* Geplantes Datum */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Geplantes Datum *</label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.scheduledDate ? errorInputStyle : {})
                }}
              />
              {errors.scheduledDate && <span style={errorTextStyle}>{errors.scheduledDate}</span>}
            </div>

            {/* Geplante Uhrzeit */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Geplante Uhrzeit *</label>
              <input
                type="time"
                value={formData.scheduledTime}
                onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.scheduledTime ? errorInputStyle : {})
                }}
              />
              {errors.scheduledTime && <span style={errorTextStyle}>{errors.scheduledTime}</span>}
            </div>

            {/* Tatsächliches Datum */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Tatsächliches Datum</label>
              <input
                type="date"
                value={formData.actualDate}
                onChange={(e) => handleInputChange('actualDate', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Tatsächliche Uhrzeit */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Tatsächliche Uhrzeit</label>
              <input
                type="time"
                value={formData.actualTime}
                onChange={(e) => handleInputChange('actualTime', e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Heimtore */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Heimtore</label>
              <input
                type="number"
                min="0"
                value={formData.homeScore}
                onChange={(e) => handleInputChange('homeScore', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.homeScore ? errorInputStyle : {})
                }}
              />
              {errors.homeScore && <span style={errorTextStyle}>{errors.homeScore}</span>}
            </div>

            {/* Auswärtstore */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Auswärtstore</label>
              <input
                type="number"
                min="0"
                value={formData.awayScore}
                onChange={(e) => handleInputChange('awayScore', e.target.value)}
                style={{
                  ...inputStyle,
                  ...(errors.awayScore ? errorInputStyle : {})
                }}
              />
              {errors.awayScore && <span style={errorTextStyle}>{errors.awayScore}</span>}
            </div>
          </div>

          {/* Notizen */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Notizen</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              style={textareaStyle}
              rows={3}
              placeholder="Zusätzliche Informationen zum Spiel..."
            />
          </div>

          <div style={buttonContainerStyle}>
            <button
              type="button"
              onClick={onCancel}
              style={cancelButtonStyle}
              disabled={isLoading}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={submitButtonStyle}
              disabled={isLoading}
            >
              {isLoading ? 'Speichern...' : (match ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '90vh',
  overflow: 'auto',
};

const headerStyle: React.CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.5rem',
  fontWeight: '600',
  color: '#1f2937',
};

const formStyle: React.CSSProperties = {
  padding: '24px',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '16px',
  marginBottom: '20px',
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const labelStyle: React.CSSProperties = {
  marginBottom: '4px',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.875rem',
  transition: 'border-color 0.15s ease-in-out',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444',
};

const errorTextStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.75rem',
  marginTop: '4px',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '24px',
  paddingTop: '20px',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
};

export default MatchForm;
